// https://github.com/arian/pngjs
import PNGReader from "png.js";
import moment from "moment";
import bbox from "@turf/bbox";

export const extractDataForDirectionVisFromResponse = response => {
  const magnitudeReader = new PNGReader(atob(response.magnitudes.data));
  const directionReader = new PNGReader(atob(response.directions.data));

  const magnitudePromise = new Promise((resolve, reject) => {
    magnitudeReader.parse(function(err, png) {
      if (err) {
        reject(err);
      }

      // cuz we don't have a meaningful way to show wave velocity yet
      const makeValueArrayWithOnesAndZeroes =
        response.magnitudes.dataSource.id === 19;

      // cuz wave data on land makes no sense
      const useZeroAtScaleEnds =
        response.magnitudes.dataSource.id === 19 ||
        response.magnitudes.dataSource.id === 24;

      // https://www.w3.org/TR/PNG-Chunks.html
      if (png.colorType !== 0)
        throw new Error("Magnitude PNG could not be parsed correctly.");

      const pngData = getDataFromPng(
        response.magnitudes.summary,
        response.magnitudes.geojsonEnvelope,
        png,
        false,
        makeValueArrayWithOnesAndZeroes,
        useZeroAtScaleEnds
      );
      resolve(pngData);
    });
  });

  const directionPromise = new Promise((resolve, reject) => {
    directionReader.parse(function(err, png) {
      if (err) reject(err);

      // cuz waves directions indicate where they come FROM, but we show arrows where they go TO
      const reverseValues = response.magnitudes.dataSource.id === 19;

      // https://www.w3.org/TR/PNG-Chunks.html
      if (png.colorType !== 0)
        throw new Error("Direction PNG could not be parsed correctly.");

      const pngData = getDataFromPng(
        response.directions.summary,
        response.directions.geojsonEnvelope,
        png,
        reverseValues,
        false
      );

      resolve(pngData);
    });
  });

  return Promise.all([
    magnitudePromise,
    directionPromise
  ]).then(([magnitude, direction]) => ({ magnitude, direction }));
};

const getDataFromPng = (
  rasterSummary,
  geojsonEnvelope,
  png,
  reverseValues = false,
  makeValueArrayWithOnesAndZeroes = false,
  useZeroAtScaleEnds = false
) => {
  const scale = (rasterSummary.max - rasterSummary.min) / 255;
  const boundingBox = bbox(geojsonEnvelope);
  const pixelsPerLonDeg = png.width / (boundingBox[2] - boundingBox[0]);
  const longitudes = [];

  for (let i = 0; i < png.width; i++) {
    longitudes.push(boundingBox[0] + i / pixelsPerLonDeg);
  }

  const pixelsPerLatDeg = png.height / (boundingBox[3] - boundingBox[1]);
  const latitudes = [];

  for (let i = 0; i < png.height; i++) {
    latitudes.push(boundingBox[3] - i / pixelsPerLatDeg);
  }

  const values = [];

  for (var y = 0; y < png.height; y++) {
    values.push([]);
    for (var x = 0; x < png.width; x++) {
      const index = x + y * png.width;

      let value = makeValueArrayWithOnesAndZeroes
        ? 1
        : rasterSummary.max - png.pixels[index] * scale;

      if (reverseValues) value = value < 180 ? value + 180 : value - 180;

      if (useZeroAtScaleEnds)
        value =
          png.pixels[index] === 0 || png.pixels[index] === 255 ? 0 : value;

      values[y].push(value);
    }
  }

  return { boundingBox, latitudes, longitudes, pixelsPerLatDeg, pixelsPerLonDeg, png, values };
};

export const getQueryTimestamp = (trailTimestamp, selectedVariableId) => {
  let queryTimestamp = trailTimestamp;
  let hycomMoment = moment(queryTimestamp).utc();
  let hourPart = hycomMoment.hour();

  if (selectedVariableId === 51) {
    // TODO: very brittle, need to remember to hardcode
    hourPart = Math.floor(hourPart / 3) * 3;
  }

  hycomMoment.set({ hour: hourPart, minute: 0, second: 0, ms: 0 });

  return hycomMoment.utc().toISOString();
};

const getClosestIndexForCoord = (coord, maxOrMinValue, pixelsPerDegOfCoord, minValue) => 
  Math.max( 
    Math.min( 
      Math.round( Math.abs( maxOrMinValue - coord ) * pixelsPerDegOfCoord ), 
      minValue
    ), 
    0 
  );

const getValueAtLongitudeLatitude = (longitude, latitude, data) => {

  const latIndex = getClosestIndexForCoord(latitude, data.boundingBox[3], data.pixelsPerLatDeg, data.png.height-1);
  const lonIndex = getClosestIndexForCoord(longitude, data.boundingBox[0], data.pixelsPerLonDeg, data.png.width-1);

  return data.values[latIndex][lonIndex];
}

export const makeParticleTrails = (
  magnitudeData,
  directionData,
  selectedOverlayData,
  boundary
) => {
  const numberOfPointsOnSide = selectedOverlayData.numberOfPointsOnSide || 50;
  const numberOfPointsInPath = selectedOverlayData.numberOfPointsInPath || 50;
  const timeIncrementMinutes = selectedOverlayData.timeIncrementMinutes || 5;

  const pathRed = 255;
  const pathGreen = 255;
  const pathBlue = 255;
  const lines = [];
  const R = 6378137;

  for (let i = 0; i < numberOfPointsOnSide; i++) {
    for (let j = 0; j < numberOfPointsOnSide; j++) {

      let longitude =
        boundary.minLongitude +
        6 +
        (i / numberOfPointsOnSide) *
          (boundary.maxLongitude - boundary.minLongitude);

      let latitude =
        boundary.minLatitude +
        (j / numberOfPointsOnSide) *
          (boundary.maxLatitude - boundary.minLatitude + 7);

      for (let p = 0; p < numberOfPointsInPath; p++) {

        const magnitude = getValueAtLongitudeLatitude(longitude, latitude, magnitudeData);
        var direction = getValueAtLongitudeLatitude(longitude, latitude, directionData);

        let cartesianAngle = -direction + 90;

        if (cartesianAngle < 0) cartesianAngle += 360;

        const cartesianAngleRadians = (cartesianAngle * Math.PI) / 180;
        const metersLongitude =
          magnitude *
          Math.cos(cartesianAngleRadians) *
          timeIncrementMinutes *
          60;
        const metersLatitude =
          magnitude *
          Math.sin(cartesianAngleRadians) *
          timeIncrementMinutes *
          60;
        const dLat = metersLatitude / R;
        const dLon =
          metersLongitude / (R * Math.cos((Math.PI * latitude) / 180));
        const alpha = Math.floor((255 * p) / numberOfPointsInPath);
        const newLongitude = longitude + (dLon * 180) / Math.PI;
        const newLatitude = latitude + (dLat * 180) / Math.PI;

        lines.push({
          color: [pathRed, pathGreen, pathBlue, alpha],
          sourcePosition: [longitude - 360, latitude],
          targetPosition: [newLongitude - 360, newLatitude]
        });
        lines.push({
          color: [pathRed, pathGreen, pathBlue, alpha],
          sourcePosition: [longitude, latitude],
          targetPosition: [newLongitude, newLatitude]
        });

        latitude = newLatitude;
        longitude = newLongitude;
      }
    }
  }

  return lines;
};
